import { useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Stack,
  Switch,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import { adminApi } from '../../api/adminApi';
import { adminResultsApi } from '../../api/adminResultsApi';
import { OverallRankingTable } from '../../components/results/OverallRankingTable';
import { PdfPrintButton } from '../../components/results/PdfPrintButton';
import { generateOverallRanking } from '../../utils/pdf/generateOverallRanking';
import { useAuth } from '../../hooks/useAuth';
import type { Competition } from '../../types/domain';

interface Props {
  competition: Competition;
}

export const CompetitionOverallTab = ({ competition }: Props) => {
  const [categoryIdx, setCategoryIdx] = useState(0);
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const isAdmin = user?.role === 'UNIT_ADMIN' || user?.role === 'SUPER_ADMIN';

  const { data: categories = [] } = useQuery({
    queryKey: ['admin', 'competition', competition.id, 'categories'],
    queryFn: () => adminApi.competitionCategories(competition.id),
  });

  const { data: overall = [], isLoading } = useQuery({
    queryKey: ['admin', 'competition', competition.id, 'overall'],
    queryFn: () => adminResultsApi.overall(competition.id),
    refetchInterval: 5_000,
  });

  const visibilityMutation = useMutation({
    mutationFn: (visible: boolean) =>
      adminApi.updateCompetition(competition.id, {
        name: competition.name,
        competitionDate: competition.competitionDate,
        location: competition.location ?? undefined,
        status: competition.status,
        npPenaltyRank: competition.npPenaltyRank,
        tieBreakerEnabled: competition.tieBreakerEnabled,
        penaltyTrackingMode: competition.penaltyTrackingMode,
        stafetaDvojicAttempts: competition.stafetaDvojicAttempts,
        stafetaDvojicParallelTracks: competition.stafetaDvojicParallelTracks,
        overallRankingVisible: visible,
      }),
    onSuccess: (_, visible) => {
      enqueueSnackbar(
        visible ? 'Celkové pořadí je nyní veřejné' : 'Celkové pořadí skryto pro veřejnost',
        { variant: 'success' },
      );
      queryClient.invalidateQueries({ queryKey: ['admin', 'competition', competition.id] });
    },
    onError: (e: any) => enqueueSnackbar(e?.response?.data?.message ?? 'Chyba', { variant: 'error' }),
  });

  const currentCategory = categories[categoryIdx];
  const currentOverall = overall.find((o) => o.categoryId === currentCategory?.id);

  return (
    <Stack spacing={2}>
      <Card>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} justifyContent="space-between">
            <Box>
              <Typography variant="h6">Veřejné zobrazení celkového pořadí</Typography>
              <Typography variant="body2" color="text.secondary">
                {competition.overallRankingVisible
                  ? 'Diváci vidí celkové pořadí v živé aplikaci.'
                  : 'Celkové pořadí je skryté — diváci vidí placeholder „ještě nezveřejněno".'}
              </Typography>
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={!!competition.overallRankingVisible}
                  onChange={(e) => visibilityMutation.mutate(e.target.checked)}
                  disabled={visibilityMutation.isPending}
                  color="success"
                />
              }
              label={
                <Stack direction="row" spacing={0.5} alignItems="center">
                  {competition.overallRankingVisible ? <VisibilityIcon /> : <VisibilityOffIcon />}
                  <Typography>{competition.overallRankingVisible ? 'Veřejné' : 'Skryto'}</Typography>
                </Stack>
              }
            />
          </Stack>
        </CardContent>
      </Card>

      <Alert severity="info" variant="outlined">
        Toto je <strong>administrátorský pohled</strong> — vidíte celkové pořadí v každém okamžiku.
        Diváci uvidí pořadí na veřejné stránce <code>/s/{competition.shortCode}</code> až po zapnutí přepínače výše.
      </Alert>

      {categories.length > 0 && (
        <Tabs value={categoryIdx} onChange={(_, v) => setCategoryIdx(v)} variant="scrollable" scrollButtons="auto">
          {categories.map((c) => (
            <Tab key={c.id} label={c.name} />
          ))}
        </Tabs>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : currentCategory && currentOverall ? (
        <Card>
          <CardContent>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="h6">
                {currentCategory.name} — celkové pořadí
              </Typography>
              {isAdmin && (
                <PdfPrintButton
                  label="Tisk PDF"
                  size="small"
                  disabled={!currentOverall.teams || currentOverall.teams.length === 0}
                  onGenerate={() =>
                    generateOverallRanking({
                      competition,
                      category: currentCategory,
                      ranking: currentOverall,
                      disciplines: currentCategory.disciplines,
                    })
                  }
                />
              )}
            </Stack>
            <OverallRankingTable ranking={currentOverall} disciplines={currentCategory.disciplines} />
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent>
            <Typography color="text.secondary">Žádná data k zobrazení.</Typography>
          </CardContent>
        </Card>
      )}
    </Stack>
  );
};
